import { END, eventChannel } from 'redux-saga';
import { call, put, race, take, takeLatest, cancelled } from 'redux-saga/effects';
import {
  CASE_ASK_AI_SSE_BEGIN,
  CASE_ASK_AI_SSE_PROGRESS,
  CASE_ASK_AI_SSE_STREAM,
  CASE_ASK_AI_SSE_COMPLETE,
  CASE_ASK_AI_SSE_ERROR,
  CASE_ASK_AI_SSE_RESET,
  CASE_ASK_AI_SSE_CANCEL,
  CASE_ASK_AI_SSE_METADATA,
  CASE_ASK_AI_SSE_CITATION,
  CASE_ASK_AI_SSE_SET_SELECTED_BUNDLES,
  CASE_ASK_AI_SSE_SET_SELECTED_FILES,
  CASE_ASK_AI_SSE_OPEN,
} from './constants';
import {
  createPromiseAction,
  resolvePromiseAction,
  rejectPromiseAction,
} from '@adobe/redux-saga-promise';
import { withCurrentCaseId } from 'common/selectors';
import api, { getAccessToken } from 'common/api';
import { fetchEventSource } from '@microsoft/fetch-event-source';

export const askAiSse = createPromiseAction(CASE_ASK_AI_SSE_BEGIN);

export const cancelAskAiSse = () => ({ type: CASE_ASK_AI_SSE_CANCEL });

export const resetAskAiSse = () => ({ type: CASE_ASK_AI_SSE_RESET });

export const openAskAiSse = () => ({ type: CASE_ASK_AI_SSE_OPEN });

export const setAskAiSseSelectedBundles = (bundles) => ({
  type: CASE_ASK_AI_SSE_SET_SELECTED_BUNDLES,
  payload: Array.isArray(bundles) ? bundles : [],
});

export const setAskAiSseSelectedFiles = ({ files, selectionType } = {}) => {
  const normalizedType = selectionType === 'compareFiles' ? 'compareFiles' : 'files';
  return {
    type: CASE_ASK_AI_SSE_SET_SELECTED_FILES,
    payload: {
      files: Array.isArray(files) ? files : [],
      selectionType: selectionType ? normalizedType : null,
    },
  };
};

const parseSsePayloads = (raw) => {
  if (!raw) return [];
  return raw
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => (line.startsWith('data:') ? line.slice(5).trim() : line))
    .map((line) => {
      try {
        return JSON.parse(line);
      } catch {
        return { type: 'text', message: line };
      }
    });
};

const progressMessageFromPayload = (payload) =>
  payload?.progress_data?.message ||
  payload?.message ||
  payload?.reason ||
  payload?.status ||
  (typeof payload === 'string' ? payload : null);

const errorMessageFromPayload = (payload) => {
  const message =
    payload?.error ||
    payload?.message ||
    payload?.detail ||
    payload?.reason ||
    payload?.status ||
    (typeof payload === 'string' ? payload : null);
  if (!message) return null;
  const normalized = String(message).trim();
  if (!normalized) return null;
  return normalized.startsWith('ERROR:') ? normalized : `ERROR: ${normalized}`;
};

const streamTextFromPayload = (payload) =>
  payload?.token ||
  payload?.output ||
  payload?.data ||
  payload?.text ||
  payload?.content ||
  payload?.message ||
  (typeof payload === 'string' ? payload : '');

const normalizeStringArray = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => String(v ?? '').trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
};

const updateHistoryEntry = (history, id, updater) =>
  (history || []).map((entry) => (entry.id === id ? updater(entry) : entry));

function createSseChannel(url, body) {
  return eventChannel((emit) => {
    const controller = new AbortController();
    let didClose = false;

    const defaultErrorMessage =
      'Unable to establish connection with the AI service. Please try again or contact support if the issue persists.';

    (async () => {
      try {
        const accessToken = getAccessToken();
        if (!accessToken) {
          emit({ kind: 'error', error: 'No access token' });
          emit(END);
          return;
        }

        await fetchEventSource(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(body || {}),
          signal: controller.signal,
          // Keep the SSE stream alive when the tab is hidden to avoid restart on refocus.
          openWhenHidden: true,
          onopen: async (response) => {
            if (!response.ok) {
              const message =
                response.status === 401
                  ? 'Your session has expired. Please sign in again.'
                  : defaultErrorMessage;
              emit({ kind: 'error', error: message });
              controller.abort();
              throw new Error(message);
            }
          },
          onmessage: (event) => {
            emit({ kind: 'message', data: event.data });
          },
          onclose: () => {
            if (didClose) return;
            emit({ kind: 'error', error: 'Connection closed unexpectedly.' });
            emit(END);
          },
          onerror: (error) => {
            if (controller.signal.aborted) return;
            const message = error?.message || defaultErrorMessage;
            emit({ kind: 'error', error: message });
            throw error;
          },
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        const message = error?.message || defaultErrorMessage;
        emit({ kind: 'error', error: message });
        emit(END);
      }
    })();

    return () => {
      didClose = true;
      controller.abort();
    };
  });
}

export function* doAskAiSse(action) {
  const {
    payload: {
      caseId,
      userId,
      input,
      outputFormat,
      include,
      bundles,
      compare,
      version,
      chatId,
      focus,
      suppressUrlUpdate,
      modelParameter,
    },
  } = action;
  const shouldUpdateUrl = !suppressUrlUpdate;

  if (!caseId || !userId || !input?.trim()) {
    const message = 'Missing case, user, or input for Ask AI SSE';
    yield put({ type: CASE_ASK_AI_SSE_ERROR, error: message });
    yield call(rejectPromiseAction, action, message);
    return;
  }

  let channel;

  try {
    const apiBaseUrl = api.getApiBaseUrl();
    const output = typeof outputFormat === 'string' ? outputFormat.trim().toLowerCase() : '';
    const includeValue = Array.isArray(include)
      ? include.map((v) => String(v ?? '').trim()).filter(Boolean)
      : typeof include === 'string'
        ? include
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : [];
    const bundlesValue = Array.isArray(bundles)
      ? bundles.map((v) => String(v ?? '').trim()).filter(Boolean)
      : typeof bundles === 'string'
        ? bundles
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : [];
    const compareValue = Array.isArray(compare)
      ? compare.map((v) => String(v ?? '').trim()).filter(Boolean)
      : typeof compare === 'string'
        ? compare
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean)
        : [];
    const caseIdSegment = encodeURIComponent(String(caseId));
    const url = `${apiBaseUrl}/cases/${caseIdSegment}/chats/send/sse`;

    // NOTE: API changed from GET+query params to POST+JSON body to allow Authorization header.
    const body = { input: String(input) };
    if (typeof version === 'string' && version.trim()) body.version = version.trim();
    if (output) body.output_format = output;
    if (includeValue.length > 0) body.include = includeValue;
    if (bundlesValue.length > 0) body.bundles = bundlesValue;
    if (compareValue.length > 0) {
      const pivot = compareValue[0];
      const comparisonSet = compareValue.filter((value) => value !== pivot);
      body.pivot = pivot;
      if (comparisonSet.length > 0) {
        body.comparison_set = comparisonSet;
      }
    }
    if (focus?.type === 'bundle' && focus?.bundle) {
      body.focus = { type: 'bundle', bundle: String(focus.bundle) };
    } else if (focus?.type === 'file' && focus?.file) {
      body.focus = { type: 'file', file: String(focus.file) };
    }
    if (chatId) body.chat_id = String(chatId);
    if (modelParameter) body.model_parameter = String(modelParameter);

    channel = yield call(createSseChannel, url, body);

    // Throttling mechanism for token streaming
    let tokenBuffer = '';
    let lastDispatchTime = 0;
    const THROTTLE_MS = 30; // Dispatch at most every 30ms (~33fps)

    const flushTokenBuffer = function* () {
      if (tokenBuffer) {
        yield put({ type: CASE_ASK_AI_SSE_STREAM, payload: { text: tokenBuffer } });
        tokenBuffer = '';
        lastDispatchTime = Date.now();
      }
    };

    try {
      while (true) {
        const { streamEvent, cancelEvent } = yield race({
          streamEvent: take(channel),
          cancelEvent: take(CASE_ASK_AI_SSE_CANCEL),
        });

        if (cancelEvent) {
          yield call(flushTokenBuffer); // Flush any remaining tokens
          channel.close();
          yield put({ type: CASE_ASK_AI_SSE_COMPLETE, payload: { cancelled: true } });
          yield call(resolvePromiseAction, action, { cancelled: true });
          break;
        }

        if (!streamEvent) continue;

        if (streamEvent === END) {
          yield call(flushTokenBuffer);
          channel.close();
          yield put({ type: CASE_ASK_AI_SSE_ERROR, error: 'Connection closed unexpectedly.' });
          yield call(rejectPromiseAction, action, 'Connection closed unexpectedly.');
          break;
        }

        if (streamEvent.kind === 'error') {
          yield call(flushTokenBuffer); // Flush any remaining tokens
          channel.close();
          yield put({ type: CASE_ASK_AI_SSE_ERROR, error: streamEvent.error });
          yield call(rejectPromiseAction, action, streamEvent.error);
          break;
        }

        const payloads = parseSsePayloads(streamEvent.data);
        if (!payloads.length) continue;

        for (const payload of payloads) {
          if (payload?.type === 'metadata') {
            yield put({ type: CASE_ASK_AI_SSE_METADATA, payload });
            const chatIdFromMetadata = payload?.chat_id || payload?.chatId;
            if (chatIdFromMetadata && shouldUpdateUrl) {
              const currentPath = window.location.pathname;
              const uuidPattern =
                /\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}\/?$/;
              let newPath = currentPath;

              if (uuidPattern.test(currentPath)) {
                newPath = currentPath.replace(uuidPattern, `/${chatIdFromMetadata}`);
              } else {
                newPath = currentPath.endsWith('/')
                  ? currentPath + chatIdFromMetadata
                  : `${currentPath}/${chatIdFromMetadata}`;
              }

              if (newPath !== currentPath) {
                window.history.replaceState(null, '', newPath + window.location.search);
              }
            }
            continue;
          }

          if (payload?.type === 'status' || payload?.type === 'progress') {
            const message = progressMessageFromPayload(payload);
            if (message) {
              yield put({
                type: CASE_ASK_AI_SSE_PROGRESS,
                payload: {
                  message,
                  stage: payload?.progress_data?.stage || payload?.stage || null,
                },
              });
            }
            continue;
          }

          if (payload?.type === 'error') {
            const message =
              errorMessageFromPayload(payload) || 'ERROR: An unexpected error occurred.';
            yield call(flushTokenBuffer);
            yield put({ type: CASE_ASK_AI_SSE_ERROR, error: message });
            channel.close();
            yield call(rejectPromiseAction, action, message);
            return;
          }

          if (payload?.type === 'token') {
            const text = streamTextFromPayload(payload);
            if (text) {
              tokenBuffer += text;
              const now = Date.now();

              // Dispatch if throttle period has elapsed
              if (now - lastDispatchTime >= THROTTLE_MS) {
                yield call(flushTokenBuffer);
              }
            }
            continue;
          }

          if (payload?.type === 'citation') {
            let sourceRaw =
              payload?.citation_object?.source ?? payload?.citation_object?.metadata?.source;

            if (sourceRaw == null && typeof payload?.citation_marker === 'string') {
              const match = payload.citation_marker.match(/Citation(?:Match)?(\d+)/);
              if (match) {
                sourceRaw = match[1];
              }
            }

            const source = Number(sourceRaw);
            if (!Number.isNaN(source) && Number.isFinite(source)) {
              yield put({
                type: CASE_ASK_AI_SSE_CITATION,
                payload: {
                  source,
                  citationObject: payload?.citation_object ?? null,
                  chatId: payload?.chat_id ?? payload?.chatId ?? null,
                },
              });
            }
            continue;
          }

          if (payload?.type === 'done' || payload?.type === 'complete') {
            yield call(flushTokenBuffer); // Flush any remaining tokens before completion
            yield put({
              type: CASE_ASK_AI_SSE_COMPLETE,
              payload: { chatId: payload?.chat_id ?? payload?.chatId },
            });
            channel.close();
            yield call(resolvePromiseAction, action, payload);
            return;
          }
          // Ignore any other event types to avoid rendering non-token content.
        }
      }
    } catch (innerError) {
      // Handle errors during stream processing
      yield call(flushTokenBuffer);
      if (channel) channel.close();
      const errorMessage =
        innerError?.message || 'An unexpected error occurred while processing the AI response.';
      yield put({ type: CASE_ASK_AI_SSE_ERROR, error: errorMessage });
      yield call(rejectPromiseAction, action, errorMessage);
    } finally {
      if (yield cancelled()) {
        yield call(flushTokenBuffer); // Flush any remaining tokens
        if (channel) channel.close();
        yield put({ type: CASE_ASK_AI_SSE_COMPLETE, payload: { cancelled: true } });
        yield call(resolvePromiseAction, action, { cancelled: true });
      }
    }
  } catch (outerError) {
    // Handle errors during setup (e.g., EventSource creation)
    if (channel) channel.close();
    const errorMessage =
      outerError?.message ||
      'Failed to connect to the AI service. Please check your network connection and try again.';
    yield put({ type: CASE_ASK_AI_SSE_ERROR, error: errorMessage });
    yield call(rejectPromiseAction, action, errorMessage);
  }
}

export function* watchAskAiSse() {
  yield takeLatest(askAiSse, withCurrentCaseId(doAskAiSse));
}

export function reducer(state, action) {
  switch (action.type) {
    case CASE_ASK_AI_SSE_SET_SELECTED_BUNDLES:
      return {
        ...state,
        askAiSseSelectedBundles: Array.isArray(action.payload) ? action.payload : [],
      };
    case CASE_ASK_AI_SSE_SET_SELECTED_FILES:
      return {
        ...state,
        askAiSseSelectedFiles: Array.isArray(action.payload?.files) ? action.payload.files : [],
        askAiSseSelectedFilesType: action.payload?.selectionType ?? null,
      };
    case CASE_ASK_AI_SSE_OPEN:
      return {
        ...state,
        askAiSseResetCount: (state.askAiSseResetCount || 0) + 1,
      };

    case CASE_ASK_AI_SSE_BEGIN + '.TRIGGER': {
      const newChatId = Date.now();
      const history = state.askAiSseHistory || [];
      const compareFiles = normalizeStringArray(action.payload?.compare);
      const compare = compareFiles.length > 0 ? { files: compareFiles } : null;
      const displayPrompt =
        typeof action.payload?.displayPrompt === 'string' && action.payload.displayPrompt.trim()
          ? action.payload.displayPrompt
          : action.payload.input;
      return {
        ...state,
        askAiSseStatus: 'loading',
        askAiSsePrompt: displayPrompt,
        askAiSseAssistantText: '',
        askAiSseCitationsBySource: {},
        askAiSseProgress: [],
        askAiSseProgressMessage: '',
        askAiSseError: null,
        askAiSseChatId: null,
        askAiSseHasFirstChunk: false,
        askAiSseCurrentId: newChatId,
        askAiSseHistory: [
          ...history,
          {
            id: newChatId,
            prompt: displayPrompt,
            assistant: '',
            citationsBySource: {},
            status: 'loading',
            error: null,
            createdAt: Date.now(),
            compare,
          },
        ],
      };
    }

    case CASE_ASK_AI_SSE_METADATA:
      return {
        ...state,
        askAiSseChatId: action.payload?.chat_id ?? action.payload?.chatId ?? null,
      };

    case CASE_ASK_AI_SSE_CITATION: {
      const currentId = state.askAiSseCurrentId;
      const source = action.payload?.source;
      if (!currentId || source == null) return state;

      const key = String(source);
      const citationObject = action.payload?.citationObject ?? null;
      const nextActiveMap = {
        ...(state.askAiSseCitationsBySource || {}),
        [key]: citationObject,
      };
      const updatedHistory = updateHistoryEntry(state.askAiSseHistory, currentId, (entry) => ({
        ...entry,
        citationsBySource: {
          ...(entry.citationsBySource || {}),
          [key]: citationObject,
        },
      }));

      return {
        ...state,
        askAiSseCitationsBySource: nextActiveMap,
        askAiSseHistory: updatedHistory,
      };
    }

    case CASE_ASK_AI_SSE_PROGRESS: {
      const nextProgress = [...state.askAiSseProgress, action.payload].slice(-20);
      return {
        ...state,
        askAiSseProgress: nextProgress,
        askAiSseProgressMessage: action.payload?.message || '',
      };
    }

    case CASE_ASK_AI_SSE_STREAM: {
      const currentId = state.askAiSseCurrentId;
      const updatedAssistant = (state.askAiSseAssistantText || '') + (action.payload?.text || '');
      const updatedHistory = updateHistoryEntry(state.askAiSseHistory, currentId, (entry) => ({
        ...entry,
        assistant: (entry.assistant || '') + (action.payload?.text || ''),
        status: 'streaming',
      }));
      return {
        ...state,
        askAiSseStatus: 'streaming',
        askAiSseAssistantText: updatedAssistant,
        askAiSseHasFirstChunk: true,
        askAiSseHistory: updatedHistory,
      };
    }

    case CASE_ASK_AI_SSE_COMPLETE:
      return {
        ...state,
        askAiSseStatus: action.payload?.cancelled ? 'idle' : 'complete',
        askAiSseProgressMessage: '',
        askAiSseHistory: updateHistoryEntry(
          state.askAiSseHistory,
          state.askAiSseCurrentId,
          (entry) => ({
            ...entry,
            status: action.payload?.cancelled ? 'cancelled' : 'complete',
            assistant: entry.assistant || state.askAiSseAssistantText || '',
          }),
        ),
      };

    case CASE_ASK_AI_SSE_ERROR:
      return {
        ...state,
        askAiSseStatus: 'error',
        askAiSseProgressMessage: '',
        askAiSseError: action.error,
        askAiSseHistory: updateHistoryEntry(
          state.askAiSseHistory,
          state.askAiSseCurrentId,
          (entry) => ({
            ...entry,
            status: 'error',
            error: action.error,
          }),
        ),
      };

    case CASE_ASK_AI_SSE_RESET:
      return {
        ...state,
        askAiSseStatus: 'idle',
        askAiSsePrompt: '',
        askAiSseAssistantText: '',
        askAiSseCitationsBySource: {},
        askAiSseProgress: [],
        askAiSseProgressMessage: '',
        askAiSseError: null,
        askAiSseChatId: null,
        askAiSseHasFirstChunk: false,
        askAiSseHistory: [],
        askAiSseCurrentId: null,
        askAiSseSelectedBundles: [],
      };

    case CASE_ASK_AI_SSE_CANCEL:
      return {
        ...state,
        askAiSseStatus: 'idle',
        askAiSseProgressMessage: '',
        askAiSseCitationsBySource: {},
        askAiSseHistory: updateHistoryEntry(
          state.askAiSseHistory,
          state.askAiSseCurrentId,
          (entry) => ({
            ...entry,
            status: entry?.status === 'complete' ? entry.status : 'cancelled',
          }),
        ),
        askAiSseCurrentId: null,
      };

    default:
      return state;
  }
}
